import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { AnyAction, Store } from 'redux';
import { PAGE_ROUTES } from '~/constants/pages';
import { ReduxState } from '~/types/redux';
import clientSide from '~/helpers/clientSide';
import { setCurrentlyInNGC } from '~/redux/actions/common';

/**
 * Hook to track and update the currentlyInNGC state based on the current route
 */
const useRouteNGCTracker = (store: Store<ReduxState, AnyAction>): void => {
  const router = useRouter();

  // Handle route changes
  useEffect(() => {
    // Only run on client side to avoid SSR issues
    if (!clientSide) return;

    const handleRouteChange = (url: string) => {
      const cleanUrl = url.replace(/\/buy/, '').replace(/\/$/, '');
      // Determine if the new route should set currentlyInNGC to true
      const isNGCRoute = [
        PAGE_ROUTES.BUILD,
        PAGE_ROUTES.RECOMMENDATION,
        PAGE_ROUTES.RECOMMENDATION_PROFILE,
        PAGE_ROUTES.CHECKOUT,
        PAGE_ROUTES.INSTALLATION,
      ].includes(cleanUrl);

      // Don't update if the state is already set to the correct value
      if (store.getState().currentlyInNGC === isNGCRoute) return;

      store.dispatch(setCurrentlyInNGC(isNGCRoute));
    };

    // Handle the initial route
    handleRouteChange(router.pathname);

    // Listen for route changes
    router.events.on('routeChangeComplete', handleRouteChange);

    // Clean up event listener
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router, store]);
};

export default useRouteNGCTracker;
